import { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { CONVERTERS_DATA } from "@/data/convertersData";
import { ConverterToolPage } from "@/components/ConverterToolPage";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
const PdfPasswordRemoverPage = lazy(() => import("@/components/PdfPasswordRemoverPage"));
import { AdminRoute } from "@/components/AdminRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Lazy load pages for code splitting (with auto-retry on stale chunks)
const Landing = lazy(() => import("./pages/Landing"));
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Help = lazy(() => import("./pages/Help"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TestPaymentsPage = lazy(() => import("./pages/admin/TestPaymentsPage"));
const EconomiaTempo = lazy(() => import("./pages/EconomiaTempo"));
const ConvertersLanding = lazy(() => import("./pages/ConvertersLanding"));
const SitemapPage = lazy(() => import("./pages/Sitemap"));
const ComingSoon = lazy(() => import("./pages/ComingSoon"));
const ClonadorNotaFiscal = lazy(() => import("./pages/ClonadorNotaFiscal"));
const ParaContadores = lazy(() => import("./pages/ParaContadores"));
const ParaEmpresas = lazy(() => import("./pages/ParaEmpresas"));
const ConversorXmlNfse = lazy(() => import("./pages/ConversorXmlNfse"));
const ConversoresSobDemanda = lazy(() => import("./pages/ConversoresSobDemanda"));
const PlanosPage = lazy(() => import("./pages/PlanosPage"));
const Blank = lazy(() => import("./pages/Blank"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: true, // Auto refetch when tab is focused
    },
  },
});

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <div className="dark">
        <HelmetProvider>
          <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/app" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/dashboard" element={<Navigate to="/app" replace />} />
                <Route path="/ajuda" element={<Help />} />
                <Route path="/economia-de-tempo" element={<EconomiaTempo />} />
                <Route path="/conversores" element={<ConvertersLanding />} />
                <Route path="/sitemap" element={<SitemapPage />} />
                <Route path="/assinatura" element={<Navigate to="/planos" replace />} />
                <Route path="/planos" element={<PlanosPage />} />
                <Route path="/admin" element={<AdminRoute><Navigate to="/app?page=users" replace /></AdminRoute>} />
                <Route path="/admin/usuarios" element={<AdminRoute><Navigate to="/app?page=users" replace /></AdminRoute>} />
                <Route path="/admin/pagamentos" element={<AdminRoute><Navigate to="/app?page=users" replace /></AdminRoute>} />
                <Route path="/admin/configuracoes" element={<AdminRoute><Navigate to="/app?page=admin-settings" replace /></AdminRoute>} />
                <Route path="/admin/planos" element={<AdminRoute><Navigate to="/app?page=admin-settings" replace /></AdminRoute>} />
                <Route path="/admin/logs" element={<AdminRoute><Navigate to="/app?page=users" replace /></AdminRoute>} />
                <Route path="/admin/pagamentos-teste" element={<AdminRoute><TestPaymentsPage /></AdminRoute>} />
                {/* Modelos por cidade desativados temporariamente até nova arquitetura. */}
                <Route path="/admin/modelos-nfse" element={<Navigate to="/app" replace />} />
                <Route path="/modelos" element={<Navigate to="/app" replace />} />

                <Route path="/conversor-xml-nfse" element={<ConversorXmlNfse />} />
                <Route path="/conversores-e-extratores-sob-demanda" element={<ConversoresSobDemanda />} />
                <Route path="/grf" element={<Navigate to="/app" replace />} />
                <Route path="/clonador-de-nota-fiscal" element={<ClonadorNotaFiscal />} />
                <Route path="/para-contadores" element={<ParaContadores />} />
                <Route path="/para-empresas" element={<ParaEmpresas />} />

                {/* Specialized: PDF password remover (real client-side logic) */}
                <Route path="/remover-senha-pdf" element={<PdfPasswordRemoverPage />} />

                {/* Extratores */}
                <Route path="/extratores" element={<Navigate to="/app?page=extractors" replace />} />
                <Route path="/extrator-xml-nfse" element={<Navigate to="/conversor-xml-nfse" replace />} />
                <Route path="/extrator-pdf" element={<Navigate to="/extratores" replace />} />
                <Route path="/extrator-fiscal" element={<Navigate to="/extratores" replace />} />

                {/* Dynamic Converter SEO Routes */}
                {Object.values(CONVERTERS_DATA)
                  .filter((tool) => tool.slug !== 'remover-senha-pdf')
                  .map((tool) => (
                    <Route
                      key={tool.slug}
                      path={`/${tool.slug}`}
                      element={<ConverterToolPage data={tool} />}
                    />
                  ))}


                {/* Blank page */}
                <Route path="/em-branco" element={<Blank />} />

                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          </TooltipProvider>
        </HelmetProvider>
      </div>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
